import {Injectable} from "@angular/core";
import {BehaviorSubject, finalize, map, noop, Observable, of, Subject, tap} from "rxjs";
import {takeUntil} from "rxjs/operators";
import cloneDeep from "lodash-es/cloneDeep";
import {NotificationService, Notification} from "@pb-labs/notification";
import {SFTPAPIService} from "@app/services/sftp-api/sftp-api.service";
import {UserInfoResponse} from "@app/services/sftp-api/models";
import {validateUserEmail} from "@app/modules/browse/components/settings/utils";

export interface UserInfo {
  businessName: string;
  email: string;
  nonVerifiedEmail: string;
  verifiedEmail: string;
  emailVerificationCode: string;
  sshKey: string;
}

@Injectable()
export class UserSettingsService {
  private _userInfo$: BehaviorSubject<UserInfo> = null;
  private _originalUserInfo$: BehaviorSubject<UserInfo> = null;
  private _userInfoLoading = false;
  private _userInfoUpdateLoading = false;
  private _userEmailVerifying = false;
  private _destroy$: Subject<void> = null;

  constructor(private sftpAPIService: SFTPAPIService, private notificationService: NotificationService) {}

  initialize(): void {
    this._userInfo$ = new BehaviorSubject<UserInfo>(null);
    this._originalUserInfo$ = new BehaviorSubject<UserInfo>(null);
    this._destroy$ = new Subject<void>();

    this._userInfoLoading = true;

    this.sftpAPIService
      .getUserInfo()
      .pipe(
        takeUntil(this._destroy$),
        map((response: UserInfoResponse) => {
          return {
            email: response.email,
            verifiedEmail: null,
            nonVerifiedEmail: null,
            emailVerificationCode: null,
            businessName: response.business_name,
            sshKey: response.ssh_key,
          };
        }),
        finalize(() => {
          this._userInfoLoading = false;
        })
      )
      .subscribe((userInfo: UserInfo) => {
        this._userInfo$.next(userInfo);
        this._originalUserInfo$.next(cloneDeep(userInfo));
      });
  }

  destroy(): void {
    this._userInfo$.complete();
    this._userInfo$ = null;

    this._originalUserInfo$.complete();
    this._originalUserInfo$ = null;

    this._userInfoLoading = false;
    this._userEmailVerifying = false;

    this._destroy$.next();
    this._destroy$.complete();
    this._destroy$ = null;
  }

  get userInfo$(): Observable<UserInfo> {
    return this._userInfo$.asObservable();
  }

  updateUserInfo(userInfo: UserInfo): void {
    this._userInfo$.next(userInfo);
  }

  get userInfoLoading(): boolean {
    return this._userInfoLoading;
  }

  get userEmailVerifying(): boolean {
    return this._userEmailVerifying;
  }

  get userInfoUpdateLoading(): boolean {
    return this._userInfoUpdateLoading;
  }

  isUserInfoChanged(): boolean {
    return (
      this._userInfo$?.value?.businessName?.trim() !== this._originalUserInfo$?.value?.businessName?.trim() ||
      this._userInfo$?.value?.email?.trim() !== this._originalUserInfo$?.value?.email?.trim()
    );
  }

  saveUserInfo(): Observable<void> {
    const userEmail = this._userInfo$.value?.email;

    if (!userEmail || validateUserEmail(userEmail)) {
      this.notificationService.showWarning(
        new Notification({
          message: "Cannot update user email. Invalid email",
          timeout: 5000,
        })
      );

      return of(null);
    }

    this._userInfoUpdateLoading = true;

    return this.sftpAPIService.updateUserEmail(userEmail).pipe(
      map(noop),
      tap(() => {
        this.updateUserInfo({...this._userInfo$.value, nonVerifiedEmail: userEmail});
        this.updateOriginalUserInfo({
          ...this._originalUserInfo$.value,
          email: userEmail,
          nonVerifiedEmail: userEmail,
        });
      }),
      finalize(() => {
        this._userInfoUpdateLoading = false;
      })
    );
  }

  verifyEmail(): Observable<void> {
    this._userEmailVerifying = true;

    return this.sftpAPIService.verifyUserEmail(this._userInfo$.value?.emailVerificationCode?.trim()).pipe(
      map(noop),
      tap(() => {
        this.updateUserInfo({
          ...this._userInfo$.value,
          nonVerifiedEmail: null,
          verifiedEmail: this._userInfo$.value?.nonVerifiedEmail,
        });

        this.updateOriginalUserInfo({
          ...this._originalUserInfo$.value,
          nonVerifiedEmail: null,
          verifiedEmail: this._userInfo$.value?.nonVerifiedEmail,
        });
      }),
      takeUntil(this._destroy$),
      finalize(() => {
        this._userEmailVerifying = false;
      })
    );
  }

  private updateOriginalUserInfo(userInfo: UserInfo): void {
    this._originalUserInfo$.next(userInfo);
  }
}
